import { inject, onBeforeUnmount, onMounted, ref } from 'vue';

import { isServerRendering } from '@/composables/utils/isServerRendering';
import { useNuxtRoute } from '@/composables/utils/useRoute';
import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { mapOptimizeNextPattern } from '@/wabunka-src/common/core/abVariant/mapOptimizeNextPattern';
import { parseOptxPreviewExperimentId } from '@/wabunka-src/common/core/abVariant/parseOptxPreviewExperimentId';
import type { AbResolutionSource } from '@/wabunka-src/common/core/abVariant/resolveAbDecision';
import { resolveAbDecision } from '@/wabunka-src/common/core/abVariant/resolveAbDecision';
import { AB_OVERRIDE_COOKIE_NAME, resolveAbOverride } from '@/wabunka-src/common/core/abVariant/resolveAbOverride';
import { parseCookieValue } from '@/wabunka-src/common/utils/cookie';
import { sendGAWabunkaAbTestAssignEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/abTestAssign';

/** ポーリング間隔（ms） */
const POLL_INTERVAL_MS = 100;
/** 既定のタイムアウト（ms）。呼び出し側が指定しなければこの値まで pending を続ける */
export const DEFAULT_AB_VARIANT_TIMEOUT_MS = 3000;

export type AbVariantState = 'pending' | 'control' | 'variant';

export interface UseAbVariantOptions {
  /** 割当待ちを打ち切るまでの時間（ms）。既に長いローディングを見せている画面ほど短くする */
  timeoutMs?: number;
}

/**
 * Optimize Next のエクスペリエンス割当をハイドレーション後にブラウザで判定する汎用 composable。
 * 判定そのものは純関数コア（`common/core/abVariant/`）へ委譲し、ここではポーリングと副作用だけを持つ。
 *
 * - `window.optimize_next.get(experimentId)` をポーリングし、実割当（0/1+）確定時のみ GA を1回送信する。
 * - 未割当（undefined）・タイムアウト・実験OFF（experimentId 空文字）は control へフォールバック（GA 送信なし）。
 * - `?ab=variant` / `?ab=control`（無ければ Cookie `wabunka_ab`）は非本番のみ強制（`resolveAbOverride`）。`?_optx_preview` はプレビュー割当。
 * - SSR では `'pending'` を返す。呼び出し側が pending 時にプレースホルダを描画すれば ClientOnly なしでもハイドレーション安全。
 *
 * @param experimentId Optimize Next のエクスペリエンス ID（空文字＝実験OFF＝常に control）。
 * @param options `timeoutMs` で割当待ちの上限を変えられる（既定 3 秒）。
 */
export const useAbVariant = (experimentId: string, options: UseAbVariantOptions = {}) => {
  const { timeoutMs = DEFAULT_AB_VARIANT_TIMEOUT_MS } = options;
  const maxAttempts = Math.max(1, Math.ceil(timeoutMs / POLL_INTERVAL_MS));

  const variant = ref<AbVariantState>('pending');
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const config = useRuntimeConfig();
  const route = useNuxtRoute();

  let timer: ReturnType<typeof setInterval> | undefined;
  let hasSent = false;

  const clearTimer = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  /**
   * バリアントを確定する。variant 反映・タイマー停止・GA 送信判定を一括で行う。
   * 送信可否の判定は純関数 `resolveAbDecision` に委譲し、ここではその結果を適用するだけにする。
   * GA は実割当（0/1+）確定時のみ・同一ライフサイクルで 1 回だけ送信する。
   */
  const resolve = (source: AbResolutionSource) => {
    const decision = resolveAbDecision(source);
    variant.value = decision.variant;
    clearTimer();
    if (decision.shouldSendGa && !hasSent) {
      hasSent = true;
      sendGAWabunkaAbTestAssignEvent(experimentId, decision.variant);
    }
  };

  onMounted(() => {
    if (isServerRendering()) return;

    // QA / E2E 用の強制オーバーライド（`?ab=variant` / `?ab=control`、無ければ Cookie `wabunka_ab`）。非本番限定で実験判定より優先する。
    // GA 送信はしない（実割当ではないため）。本番・staging では resolveAbOverride が常に null を返す。
    const override = resolveAbOverride(config.public.env, route.query.ab, parseCookieValue(document.cookie, AB_OVERRIDE_COOKIE_NAME));
    if (override) {
      resolve({ type: 'override', variant: override });
      return;
    }

    // Optimize Next の標準プレビュー（`?_optx_preview=<experimentId>-<pattern>`）モードか。
    // プレビューでは「どの実験を見るか」を URL が決めるため、引数の experimentId には依存せず URL から取り出す。
    // 実割当ではないので GA は送らない。
    const previewExperimentId = parseOptxPreviewExperimentId(window.location.search);
    const isOptxPreview = previewExperimentId !== null;

    // 実験OFF（ID 未設定）→ 即 control。GA 送信なし。ただしプレビュー時は experimentId が空でも継続する。
    if (!isOptxPreview && !experimentId) {
      resolve({ type: 'experimentOff' });
      return;
    }

    let attempts = 0;
    timer = setInterval(() => {
      attempts += 1;
      const optimizeNext = window.optimize_next;

      // API 未準備（GTM 非同期読み込み中）→ タイムアウトまで pending 継続
      if (!optimizeNext) {
        if (attempts >= maxAttempts) resolve({ type: 'timeout' });
        return;
      }

      if (isOptxPreview) {
        // プレビュー割当は bare ID `get(<id>)` で返る。念のため `<id>_latest` もフォールバックで読む。
        // 確定したら GA を送らずに反映（QA 用途のため）。
        const previewPattern = mapOptimizeNextPattern(optimizeNext.get(previewExperimentId) ?? optimizeNext.get(`${previewExperimentId}_latest`));
        if (previewPattern !== null) {
          resolve({ type: 'preview', variant: previewPattern });
          return;
        }
        // プレビュー割当が未準備（適用前）→ タイムアウトまで待つ（早期 control 落ちを避ける）
        if (attempts >= maxAttempts) resolve({ type: 'timeout' });
        return;
      }

      // 0/1+ の実割当 → 確定 + GA 送信。未割当（null）→ 即 control・GA 送信なし。
      resolve({ type: 'assignment', pattern: optimizeNext.get(experimentId) });
    }, POLL_INTERVAL_MS);
  });

  onBeforeUnmount(clearTimer);

  return { variant };
};
